import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";

interface Container {
  id: number;
  containerNumber: string;
  poNumber: string;
  operationStatus: "InTransit" | "AtVendor" | "Complete" | "NoActivity";
  editable: boolean;
}

const COLUMNS: Array<{ status: Container["operationStatus"]; label: string }> = [
  { status: "InTransit", label: "In Production" },
  { status: "AtVendor", label: "In Transit" },
  { status: "Complete", label: "Received" },
];

/** Production Order / WIP — Kanban board with the corrected two-stage
 * permission rule (outer vendor-visibility gate, then containerLocation ==
 * vendorNo, with a conditional-not-unconditional SBD override). */
export function Wip() {
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["wip"], queryFn: () => api.get<{ rows: Container[] }>("/prod-order").then((r) => r.rows) });

  const expediteMutation = useMutation({
    mutationFn: (containerIds: number[]) => api.post("/prod-order/expedite", { containerIds, priority: "High" }),
    onSuccess: () => {
      toast("SBD Expedite action (domain-gated) triggered");
      qc.invalidateQueries({ queryKey: ["wip"] });
    },
    onError: () => toast("Expedite failed", "error"),
  });

  const rows = data ?? [];

  return (
    <Card className="p-5">
      <div className="font-semibold text-sm mb-4">Production Order / WIP Containers</div>
      <div className="grid md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => (
          <div key={col.status}>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{col.label}</div>
            <div className="space-y-2">
              {rows
                .filter((r) => r.operationStatus === col.status)
                .map((r) => (
                  <div key={r.id} className="border border-slate-200 dark:border-white/10 rounded-lg p-3 text-sm">
                    <div className="font-medium">{r.containerNumber}</div>
                    <div className="text-xs text-slate-400">PO {r.poNumber}</div>
                    {col.status === "InTransit" && !user?.isSupplier && (
                      <Button variant="outline" icon={Zap} className="mt-2" onClick={() => expediteMutation.mutate([r.id])}>
                        Expedite
                      </Button>
                    )}
                  </div>
                ))}
              {!rows.filter((r) => r.operationStatus === col.status).length && <div className="text-xs text-slate-400 italic">No containers</div>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
